/**
 * 画面が固まった瞬間を記録して、端末だけでログを持ち出せるようにする（PRODUCT_DEV-1115・DEV 専用）。
 *
 * ある Android 端末でだけ、画面全体が約10秒固まる（スクロールもできず、タップは溜まって
 * 動き出した瞬間にまとめて処理される）。メインスレッドが何かにかかりきりになっている形だが、
 * RUM には Android の記録が届かず、USB デバッグもすぐには使えない。そこで、
 *
 * - 固まり（3 秒以上フレームが出ない）を検知し、**どのスクリプトの何が何秒止めたか**を残す
 *   （`long-animation-frame`。非対応なら `longtask`。JS 以外の止まりも拾えるよう rAF の間隔も見る）
 * - console の直近の出力をリングバッファに残す
 * - それらを1本のテキストにまとめ、画面のボタンからクリップボードへコピーできるようにする
 *
 * 固まっている最中は何も押せないので、コピーは動き出した直後に出るカードから行う
 * （`components/shared/FreezeReportCard.tsx`）。
 *
 * **DEV でだけ動かす。** 呼び出し側（main.tsx）が `ENV.VITE_ENV === "dev"` で判定する。
 */

import {
  getBreadcrumbs,
  getPendingChunks,
  getRouterState,
  safeStringify,
} from "./navDiagnostics";
import { getNavStore } from "./nav/backTracker";

/** これ以上フレームが出なかったら「固まった」とみなす。 */
export const FREEZE_THRESHOLD_MS = 3_000;
/** console の保持件数。 */
export const CONSOLE_LIMIT = 300;
/** console 1件の上限文字数。 */
export const CONSOLE_ENTRY_MAX_CHARS = 500;
/** 固まりの保持件数。 */
export const FREEZE_LIMIT = 10;
/** 1つの固まりに残すスクリプトの数（長い順）。 */
export const FREEZE_SCRIPTS_LIMIT = 10;
/** レポート全体の上限。Slack の1投稿（4万字）に収める。 */
export const REPORT_MAX_CHARS = 35_000;

export type ConsoleLevel = "log" | "info" | "warn" | "error";

export interface ConsoleEntry {
  /** `performance.now()`（ページ読み込みからのミリ秒） */
  at: number;
  level: ConsoleLevel | "uncaught" | "rejection";
  text: string;
}

export interface FreezeScript {
  sourceURL: string;
  sourceFunctionName: string;
  invoker: string;
  invokerType: string;
  durationMs: number;
  forcedStyleAndLayoutMs: number;
}

export type FreezeSource = "long-animation-frame" | "longtask" | "raf-gap";

export interface FreezeRecord {
  id: number;
  /** 固まり始め（`performance.now()`） */
  startAt: number;
  durationMs: number;
  /** どの仕組みで気づいたか（複数で気づいたら全部） */
  sources: FreezeSource[];
  /** 長い順。`long-animation-frame` のときだけ入る */
  scripts: FreezeScript[];
  /** フレームのうちスクリプトの外（描画・レイアウトなど）に使った時間。分かるときだけ */
  renderMs: number | null;
  /** 気づいたときの画面のパス */
  path: string;
}

// ---------------------------------------------------------------------------
// 純粋関数（テストする部分）
// ---------------------------------------------------------------------------

/** 上限を超えたら古いものから捨てる。 */
export function pushRing<T>(ring: T[], item: T, limit: number): void {
  ring.push(item);
  if (ring.length > limit) ring.splice(0, ring.length - limit);
}

/** Slack に貼るので、ログに紛れたトークンらしきものは伏せる。 */
export function redact(text: string): string {
  return text
    .replace(/eyJ[\w-]{8,}\.[\w-]{8,}\.[\w-]{8,}/g, "[jwt]")
    .replace(/(Bearer\s+)[\w.~+/=-]{16,}/gi, "$1[token]")
    .replace(
      /((?:access|id|refresh)_?token|authorization|password|secret|api_?key)(["']?\s*[:=]\s*["']?)[^"'\s,}]{6,}/gi,
      "$1$2[redacted]",
    );
}

function clip(text: string, max: number): string {
  return text.length > max ? `${text.slice(0, max)}…` : text;
}

/** 1つのオブジェクトを文字列にするとき、たどる値の数の上限。大きなデータが console に出ても重くしない */
export const CONSOLE_OBJECT_MAX_NODES = 200;

const TOO_BIG = Symbol("too-big");

/**
 * オブジェクトを文字列にする。たどる値が上限を超えたら途中で打ち切る
 * （`safeStringify` は全部を文字列にしてから切るので、大きなデータだとその都度重い）。
 */
export function boundedStringify(value: object): string {
  let nodes = 0;
  const seen = new WeakSet<object>();
  try {
    return (
      JSON.stringify(value, (_key, val) => {
        nodes += 1;
        if (nodes > CONSOLE_OBJECT_MAX_NODES) throw TOO_BIG;
        if (typeof val === "object" && val !== null) {
          if (seen.has(val)) return "[circular]";
          seen.add(val);
        }
        return val;
      }) ?? ""
    );
  } catch (e) {
    const name = (value as { constructor?: { name?: string } }).constructor?.name ?? "Object";
    return e === TOO_BIG ? `[${name}: 大きいので省略]` : "[unserializable]";
  }
}

/** console の引数を1行の文字列にする。 */
export function formatConsoleArgs(args: unknown[]): string {
  const parts = args.map((arg) => {
    if (typeof arg === "string") return arg;
    if (arg instanceof Error) return arg.stack ?? `${arg.name}: ${arg.message}`;
    if (arg === undefined) return "undefined";
    if (typeof arg === "function") return `[function ${arg.name || "anonymous"}]`;
    if (typeof arg !== "object" || arg === null) return String(arg);
    return boundedStringify(arg);
  });
  return clip(redact(parts.join(" ")), CONSOLE_ENTRY_MAX_CHARS);
}

/** `long-animation-frame` のエントリから、残すスクリプトを長い順に取り出す。 */
export function scriptsFromLoaf(entry: unknown): FreezeScript[] {
  const scripts = (entry as { scripts?: unknown[] }).scripts;
  if (!Array.isArray(scripts)) return [];
  return scripts
    .map((s) => {
      const x = s as Record<string, unknown>;
      return {
        sourceURL: String(x.sourceURL ?? ""),
        sourceFunctionName: String(x.sourceFunctionName ?? ""),
        invoker: String(x.invoker ?? ""),
        invokerType: String(x.invokerType ?? ""),
        durationMs: Math.round(Number(x.duration ?? 0)),
        forcedStyleAndLayoutMs: Math.round(
          Number(x.forcedStyleAndLayoutDuration ?? 0),
        ),
      };
    })
    .sort((a, b) => b.durationMs - a.durationMs)
    .slice(0, FREEZE_SCRIPTS_LIMIT);
}

/**
 * 同じ固まりを別の仕組みでも検知したら1件にまとめる。
 * 区間が重なるか、始まりが1秒以内なら同じ固まりとみなす。
 * まとめた先の記録を返す（まとめなかったら null）。
 */
export function mergeFreeze(
  list: FreezeRecord[],
  incoming: Omit<FreezeRecord, "id">,
): FreezeRecord | null {
  const end = incoming.startAt + incoming.durationMs;
  const same = list.find((f) => {
    const fEnd = f.startAt + f.durationMs;
    const overlap = incoming.startAt < fEnd && f.startAt < end;
    return overlap || Math.abs(f.startAt - incoming.startAt) < 1_000;
  });
  if (!same) return null;
  for (const s of incoming.sources) {
    if (!same.sources.includes(s)) same.sources.push(s);
  }
  if (incoming.scripts.length > 0 && same.scripts.length === 0) {
    same.scripts = incoming.scripts;
  }
  if (same.renderMs === null) same.renderMs = incoming.renderMs;
  if (incoming.durationMs > same.durationMs) {
    same.startAt = Math.min(same.startAt, incoming.startAt);
    same.durationMs = incoming.durationMs;
  }
  return same;
}

function sec(ms: number): string {
  return `${(ms / 1000).toFixed(1)}s`;
}

/** `performance.now()` の値を、レポートを作った時刻からの「◯秒前」にする。 */
function ago(at: number, now: number): string {
  return `-${sec(Math.max(0, now - at))}`;
}

export interface ReportInput {
  now: number;
  createdAt: string;
  header: Record<string, string>;
  freezes: FreezeRecord[];
  consoleEntries: ConsoleEntry[];
  /** 画面遷移のパンくず・ルーターの状態など。そのまま JSON で載せる */
  context: Record<string, unknown>;
}

function formatFreeze(f: FreezeRecord, now: number): string {
  const lines = [
    `#${f.id} ${sec(f.durationMs)} 固まり（${ago(f.startAt, now)}・${f.path}・検知: ${f.sources.join(", ")}）`,
  ];
  if (f.renderMs !== null) lines.push(`  スクリプト外（描画・レイアウトなど）: ${f.renderMs}ms`);
  if (f.scripts.length === 0) {
    lines.push(
      f.sources.includes("long-animation-frame")
        ? "  スクリプトの記録なし"
        : "  スクリプトの内訳なし（long-animation-frame で拾えていない＝JS 以外の止まりかもしれない）",
    );
  }
  for (const s of f.scripts) {
    lines.push(
      `  ${s.durationMs}ms ${s.invokerType} ${s.invoker} → ${s.sourceFunctionName || "(anonymous)"} @ ${s.sourceURL || "(unknown)"}` +
        (s.forcedStyleAndLayoutMs > 0 ? `（強制レイアウト ${s.forcedStyleAndLayoutMs}ms）` : ""),
    );
  }
  return lines.join("\n");
}

/**
 * 貼り付け用のテキストを作る。上限を超えたら古い console から削る
 * （固まりの記録と文脈は残す）。
 */
export function buildReport(input: ReportInput, maxChars = REPORT_MAX_CHARS): string {
  const head = [
    "【Unizo 固まりログ】PRODUCT_DEV-1115",
    `作成: ${input.createdAt}`,
    ...Object.entries(input.header).map(([k, v]) => `${k}: ${v}`),
    "",
    `== 固まり（${input.freezes.length}件・${sec(FREEZE_THRESHOLD_MS)}以上） ==`,
    input.freezes.length === 0
      ? "（まだ検知していません）"
      : input.freezes.map((f) => formatFreeze(f, input.now)).join("\n"),
    "",
    "== 文脈 ==",
    ...Object.entries(input.context).map(
      ([k, v]) => `${k}: ${typeof v === "string" ? v : safeStringify(v, 6_000)}`,
    ),
    "",
  ].join("\n");

  const consoleLines = input.consoleEntries.map(
    (e) => `${ago(e.at, input.now)} [${e.level}] ${e.text}`,
  );
  const title = (dropped: number) =>
    `== console（新しいもの ${consoleLines.length - dropped}件${dropped > 0 ? `・古い ${dropped}件は省略` : ""}） ==`;

  let dropped = 0;
  let body = consoleLines.join("\n");
  let text = `${head}\n${title(0)}\n${body}`;
  while (text.length > maxChars && dropped < consoleLines.length) {
    dropped += 1;
    body = consoleLines.slice(dropped).join("\n");
    text = `${head}\n${title(dropped)}\n${body}`;
  }
  return text.length > maxChars ? `${text.slice(0, maxChars)}…[truncated]` : text;
}

// ---------------------------------------------------------------------------
// 記録（ブラウザで動く部分）
// ---------------------------------------------------------------------------

const consoleEntries: ConsoleEntry[] = [];
const freezes: FreezeRecord[] = [];
let nextId = 1;
let installed = false;

type Listener = () => void;
const listeners = new Set<Listener>();

/** 画面側（useSyncExternalStore）が読む状態。変わるたびに作り直す。 */
export interface FreezeState {
  /** 最後に検知した固まり。カードの表示に使う */
  latest: FreezeRecord | null;
  /** 色帯の長押しなどで「開いて」と頼まれた回数。増えたらカードを開く */
  openRequests: number;
}

let state: FreezeState = { latest: null, openRequests: 0 };

function emit(next: Partial<FreezeState>): void {
  state = { ...state, ...next };
  for (const l of listeners) l();
}

export function subscribeFreeze(listener: Listener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function getFreezeState(): FreezeState {
  return state;
}

export function isFreezeRecorderInstalled(): boolean {
  return installed;
}

/** 固まりを検知していなくてもカードを開く（色帯の長押し）。 */
export function requestFreezeReport(): void {
  emit({ openRequests: state.openRequests + 1 });
}

function currentPath(): string {
  try {
    return location.pathname + location.search;
  } catch {
    return "";
  }
}

function recordFreeze(incoming: Omit<FreezeRecord, "id">): void {
  const merged = mergeFreeze(freezes, incoming);
  if (merged) {
    emit({ latest: { ...merged } });
    return;
  }
  const record: FreezeRecord = { id: nextId++, ...incoming };
  pushRing(freezes, record, FREEZE_LIMIT);
  // console にも出しておく（USB デバッグでつないだときにも見える）
  console.warn(
    `[freeze] ${sec(record.durationMs)} ${record.path} (${record.sources.join(",")})`,
  );
  emit({ latest: { ...record } });
}

function captureConsole(): void {
  const levels: ConsoleLevel[] = ["log", "info", "warn", "error"];
  for (const level of levels) {
    const original = console[level].bind(console);
    console[level] = (...args: unknown[]) => {
      try {
        pushRing(
          consoleEntries,
          { at: Math.round(performance.now()), level, text: formatConsoleArgs(args) },
          CONSOLE_LIMIT,
        );
      } catch {
        // 記録に失敗しても元の出力は止めない
      }
      original(...args);
    };
  }
  window.addEventListener("error", (e) => {
    pushRing(
      consoleEntries,
      {
        at: Math.round(performance.now()),
        level: "uncaught",
        text: formatConsoleArgs([e.error ?? e.message, `@ ${e.filename}:${e.lineno}:${e.colno}`]),
      },
      CONSOLE_LIMIT,
    );
  });
  window.addEventListener("unhandledrejection", (e) => {
    pushRing(
      consoleEntries,
      { at: Math.round(performance.now()), level: "rejection", text: formatConsoleArgs([e.reason]) },
      CONSOLE_LIMIT,
    );
  });
}

function observe(type: string, onEntry: (entry: PerformanceEntry) => void): boolean {
  if (
    typeof PerformanceObserver === "undefined" ||
    !PerformanceObserver.supportedEntryTypes?.includes(type)
  ) {
    return false;
  }
  try {
    new PerformanceObserver((list) => {
      for (const entry of list.getEntries()) onEntry(entry);
    }).observe({ type, buffered: true });
    return true;
  } catch {
    return false;
  }
}

function watchLongFrames(): string {
  const loaf = observe("long-animation-frame", (entry) => {
    if (entry.duration < FREEZE_THRESHOLD_MS) return;
    const scripts = scriptsFromLoaf(entry);
    const scriptTotal = scripts.reduce((sum, s) => sum + s.durationMs, 0);
    recordFreeze({
      startAt: Math.round(entry.startTime),
      durationMs: Math.round(entry.duration),
      sources: ["long-animation-frame"],
      scripts,
      renderMs: Math.max(0, Math.round(entry.duration) - scriptTotal),
      path: currentPath(),
    });
  });
  if (loaf) return "long-animation-frame";
  const longtask = observe("longtask", (entry) => {
    if (entry.duration < FREEZE_THRESHOLD_MS) return;
    recordFreeze({
      startAt: Math.round(entry.startTime),
      durationMs: Math.round(entry.duration),
      sources: ["longtask"],
      scripts: [],
      renderMs: null,
      path: currentPath(),
    });
  });
  return longtask ? "longtask" : "なし";
}

/**
 * rAF の間隔で見張る。JS 以外（描画・GPU・ブラウザ側）の止まりも拾える。
 * 画面が裏に回ると rAF は止まるので、見えていない間をまたいだ間隔は数えない。
 */
function watchFrameGaps(): void {
  let last = performance.now();
  let hiddenSinceLast = document.visibilityState !== "visible";
  document.addEventListener("visibilitychange", () => {
    hiddenSinceLast = true;
  });
  const tick = (now: number) => {
    const gap = now - last;
    if (!hiddenSinceLast && document.visibilityState === "visible" && gap >= FREEZE_THRESHOLD_MS) {
      recordFreeze({
        startAt: Math.round(last),
        durationMs: Math.round(gap),
        sources: ["raf-gap"],
        scripts: [],
        renderMs: null,
        path: currentPath(),
      });
    }
    last = now;
    hiddenSinceLast = document.visibilityState !== "visible";
    requestAnimationFrame(tick);
  };
  requestAnimationFrame(tick);
}

let observerKind = "なし";

/** main.tsx から1回だけ呼ぶ。2回目以降は何もしない。 */
export function installFreezeRecorder(): void {
  if (installed || typeof window === "undefined") return;
  installed = true;
  captureConsole();
  observerKind = watchLongFrames();
  watchFrameGaps();
}

function mainScriptName(): string {
  const src = [...document.querySelectorAll<HTMLScriptElement>("script[src]")]
    .map((s) => s.src)
    .find((s) => /\/assets\/index-[^/]+\.js$/.test(s));
  return src ? src.slice(src.lastIndexOf("/") + 1) : "(不明)";
}

async function serviceWorkerSummary(): Promise<string> {
  try {
    if (!("serviceWorker" in navigator)) return "非対応";
    const reg = await navigator.serviceWorker.getRegistration();
    return safeStringify({
      controller: Boolean(navigator.serviceWorker.controller),
      active: reg?.active?.scriptURL ?? null,
      waiting: Boolean(reg?.waiting),
      installing: Boolean(reg?.installing),
    });
  } catch {
    return "(取得できず)";
  }
}

/**
 * いま持っている記録からレポートを作る。
 * 画面遷移の記録は「画面の種類とパス」だけにする（タイトルには商談名・顧客名が入るため貼らない）。
 */
export async function createFreezeReport(): Promise<string> {
  const store = getNavStore();
  const recentNav = Object.values(store.entries)
    .slice(-10)
    .map((e) => ({ screen: e.screen, identity: e.identity, transient: e.transient }));
  const memory = (performance as unknown as { memory?: Record<string, number> }).memory;
  return buildReport({
    now: performance.now(),
    createdAt: new Date().toISOString(),
    header: {
      環境: "dev",
      ビルド: mainScriptName(),
      画面: currentPath(),
      端末: navigator.userAgent,
      表示: `${window.innerWidth}x${window.innerHeight} @${window.devicePixelRatio}・${
        matchMedia("(display-mode: standalone)").matches ? "ホーム画面から" : "ブラウザのタブ"
      }`,
      回線: navigator.onLine ? "オンライン" : "オフライン",
      検知の仕組み: `${observerKind} + raf-gap`,
      開いてから: sec(performance.now()),
    },
    freezes: [...freezes],
    consoleEntries: [...consoleEntries],
    context: {
      パンくず: getBreadcrumbs(),
      読み込み中のチャンク: getPendingChunks(),
      ルーター: getRouterState(),
      直近の画面: recentNav,
      ServiceWorker: await serviceWorkerSummary(),
      メモリ: memory
        ? {
            usedMB: Math.round(memory.usedJSHeapSize / 1e6),
            totalMB: Math.round(memory.totalJSHeapSize / 1e6),
            limitMB: Math.round(memory.jsHeapSizeLimit / 1e6),
          }
        : "(取得できず)",
    },
  });
}

/** テスト用。 */
export function resetFreezeRecorderForTest(): void {
  consoleEntries.length = 0;
  freezes.length = 0;
  nextId = 1;
  state = { latest: null, openRequests: 0 };
  listeners.clear();
}
